import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Users, UserPlus, Pencil, KeyRound, Trash2, Power, Copy, X, Crown,
  Loader2, BarChart3, Activity, ShieldCheck, Check, MessageSquare, Calendar,
  Database, FileText, Search, Mail, MailCheck, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/* ---------------- helpers ---------------- */
function timeAgo(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function initialsOf(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] || "?").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
}

const labelStyle = { display: "block", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-dim)", marginBottom: 6 };

/* ---------------- small components ---------------- */
function Modal({ eyebrow, onClose, children, testid, maxWidth = 460 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid={testid} onMouseDown={onClose} style={{
      position: "fixed", inset: 0, zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(12,12,16,0.55)", backdropFilter: "blur(4px)", padding: 24,
    }}>
      <div onMouseDown={(e) => e.stopPropagation()} className="fade-up" style={{
        width: "100%", maxWidth, background: "var(--panel)", border: "1px solid var(--hairline)",
        borderRadius: 16, boxShadow: "0 24px 60px rgba(0,0,0,0.35)", overflow: "hidden",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px 0" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ink)", fontWeight: 600 }}>{eyebrow}</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-dim)" }} aria-label="Close"><X size={18} /></button>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const admin = role === "super_admin";
  return (
    <span className="badge" style={{
      background: admin ? "rgba(107,92,231,0.16)" : "rgba(130,130,140,0.12)",
      color: admin ? "var(--ph-brand-violet)" : "var(--ink-soft)",
      border: `1px solid ${admin ? "rgba(107,92,231,0.4)" : "var(--hairline)"}`,
      display: "inline-flex", alignItems: "center", gap: 5,
    }}>
      {admin && <Crown size={11} />}{admin ? "Super Admin" : "Member"}
    </span>
  );
}

function StatusPill({ active }) {
  const c = active ? "var(--ph-product-teal)" : "#E5564E";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: active ? "var(--ink-soft)" : "#E5564E" }}>
      <span style={{ width: 7, height: 7, borderRadius: 50, background: c, boxShadow: `0 0 8px ${c}88` }} />
      {active ? "Active" : "Deactivated"}
    </span>
  );
}

function IconBtn({ icon: Icon, title, onClick, testid, danger }) {
  return (
    <button className="btn btn-ghost" onClick={onClick} title={title} data-testid={testid}
      style={{ padding: 7, color: danger ? "#E5564E" : "var(--ink-dim)" }}>
      <Icon size={16} />
    </button>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="panel" data-testid={testid} style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-dim)" }}>{label}</span>
        <Icon size={16} style={{ color: accent || "var(--ph-product-teal)" }} />
      </div>
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 42, lineHeight: 1, color: "var(--ink)" }}>{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, n, title }) {
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--ink-dim)" }}>
      <Icon size={13} /> {n}
    </span>
  );
}

/* ---------------- Add / Edit forms ---------------- */
function AddUserForm({ onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/users", { email, name: name || email.split("@")[0], title, role });
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the account.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <h3 style={{ marginBottom: 2 }}>Add a team member</h3>
      <div className="text-soft" style={{ fontSize: 13, lineHeight: 1.5, marginTop: -6 }}>
        We&apos;ll email them an invite with a login link and temporary password. They&apos;ll set their own on first sign-in.
      </div>
      <div>
        <label style={labelStyle}>Work email</label>
        <input className="field" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="name@playershealth.com" data-testid="admin-add-email" autoFocus />
      </div>
      <div>
        <label style={labelStyle}>Full name</label>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe" data-testid="admin-add-name" />
      </div>
      <div>
        <label style={labelStyle}>Title / role at company</label>
        <input className="field" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Claims Analyst" data-testid="admin-add-title" />
      </div>
      <div>
        <label style={labelStyle}>Permission level</label>
        <select className="field" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-add-role">
          <option value="member">Member — standard access</option>
          <option value="super_admin">Super Admin — full control</option>
        </select>
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy} data-testid="admin-add-submit"
        style={{ justifyContent: "center", padding: "11px 16px", marginTop: 4 }}>
        {busy ? <Loader2 size={15} className="spin" /> : <UserPlus size={15} />}
        {busy ? "Creating…" : "Create account"}
      </button>
    </form>
  );
}

function EditUserForm({ user, isSelf, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [title, setTitle] = useState(user.title || "");
  const [role, setRole] = useState(user.role || "member");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.patch(`/admin/users/${user.user_id}`, { name, title, role });
      onSaved(r.data);
      toast.success("Changes saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <h3 style={{ marginBottom: 2 }}>Edit member</h3>
      <div className="text-soft" style={{ fontSize: 13, marginTop: -6 }}>{user.email}</div>
      <div>
        <label style={labelStyle}>Full name</label>
        <input className="field" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-edit-name" />
      </div>
      <div>
        <label style={labelStyle}>Title / role at company</label>
        <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-edit-title" />
      </div>
      <div>
        <label style={labelStyle}>Permission level</label>
        <select className="field" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-edit-role" disabled={isSelf}>
          <option value="member">Member — standard access</option>
          <option value="super_admin">Super Admin — full control</option>
        </select>
        {isSelf && <div className="text-dim" style={{ fontSize: 11.5, marginTop: 6 }}>You can&apos;t change your own permission level.</div>}
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy} data-testid="admin-edit-save"
        style={{ justifyContent: "center", padding: "11px 16px", marginTop: 4 }}>
        {busy ? <Loader2 size={15} className="spin" /> : <Check size={15} />}
        {busy ? "Saving…" : "Save changes"}
      </button>
    </form>
  );
}

function TempPasswordView({ data, onClose }) {
  const isReset = data.mode === "reset";
  const sent = !!data.email_sent;
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.temp_password); toast.success("Copied to clipboard."); }
    catch { toast("Select and copy the password manually."); }
  };
  const heading = sent
    ? (isReset ? "Reset email sent" : "Invitation sent")
    : (isReset ? "Temporary password ready" : "Account ready");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="admin-temp-view">
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: sent ? "rgba(76,214,190,0.16)" : "rgba(78,205,196,0.14)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {sent ? <MailCheck size={22} style={{ color: "var(--ph-product-teal)" }} />
              : <KeyRound size={22} style={{ color: "var(--ph-product-teal)" }} />}
      </div>
      <h3 style={{ marginBottom: 0 }}>{heading}</h3>

      {sent ? (
        <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6 }} data-testid="admin-email-sent-msg">
          We emailed {isReset ? "a password reset" : "an invitation"} with the login link and temporary password to{" "}
          <strong style={{ color: "var(--ink)" }}>{data.email}</strong>. They&apos;ll set their own password on first sign-in.
        </div>
      ) : (
        <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          Email delivery isn&apos;t available, so share this with <strong style={{ color: "var(--ink)" }}>{data.email}</strong> directly.
          They&apos;ll set their own password on first sign-in. This is the only time you&apos;ll see it.
        </div>
      )}

      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-dim)", marginBottom: 6 }}>
          {sent ? "Temporary password (backup)" : "Temporary password"}
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          padding: "12px 14px", background: "var(--panel-2)", border: "1px solid var(--hairline)", borderRadius: 10,
        }}>
          <code data-testid="admin-temp-value" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "var(--ink)", letterSpacing: "0.04em" }}>
            {data.temp_password}
          </code>
          <button className="btn btn-ghost" onClick={copy} data-testid="admin-temp-copy" style={{ padding: 8 }} title="Copy">
            <Copy size={15} />
          </button>
        </div>
      </div>

      <button className="btn btn-primary" onClick={onClose} data-testid="admin-temp-close" style={{ justifyContent: "center", padding: "11px 16px" }}>
        Done
      </button>
    </div>
  );
}

/* ---------------- Team tab ---------------- */
function TeamTab({ users, reload, selfId, onShowTemp }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [query, setQuery] = useState("");

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Member deactivated." : "Member reactivated.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    }
  };
  const resetPw = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.user_id}/reset-password`);
      onShowTemp({ ...r.data, mode: "reset" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reset password.");
    }
  };
  const resendInvite = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.user_id}/resend-invite`);
      onShowTemp({ ...r.data, mode: "invite" });
      if (r.data.email_sent) toast.success(`Invitation emailed to ${u.email}.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend the invite.");
    }
  };
  const doDelete = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Account deleted.");
      setConfirmDel(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
    }
  };

  const filtered = users.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.title || "").toLowerCase().includes(q);
  });

  const COLS = "minmax(220px,2.2fr) 130px 120px 110px 150px 160px";

  return (
    <div data-testid="admin-team-tab">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 220, maxWidth: 360 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-dim)" }} />
          <input className="field" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search team…"
            data-testid="admin-search" style={{ paddingLeft: 34 }} />
        </div>
        <button className="btn btn-primary" onClick={() => setAdding(true)} data-testid="admin-add-user-btn" style={{ padding: "11px 16px" }}>
          <UserPlus size={16} /> Add team member
        </button>
      </div>

      <div className="panel" style={{ padding: 0, overflow: "hidden" }}>
       <div className="table-scroll-x">
        {/* header row */}
        <div className="admin-row-team" style={{
          display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "12px 20px",
          borderBottom: "1px solid var(--hairline)", fontSize: 10.5, letterSpacing: "0.12em",
          textTransform: "uppercase", color: "var(--ink-dim)",
        }}>
          <div>Member</div><div>Role</div><div>Status</div><div>Last active</div><div>Activity</div><div style={{ textAlign: "right" }}>Manage</div>
        </div>

        {filtered.length === 0 && (
          <div className="text-dim" data-testid="admin-team-empty" style={{ padding: 28, textAlign: "center", fontSize: 13 }}>
            No team members match.
          </div>
        )}

        {filtered.map((u) => {
          const isSelf = u.user_id === selfId;
          return (
            <div key={u.user_id} className="admin-row-team" data-testid={`admin-user-row-${u.user_id}`} style={{
              display: "grid", gridTemplateColumns: COLS, gap: 12, alignItems: "center",
              padding: "14px 20px", borderBottom: "1px solid var(--hairline-soft)",
              opacity: u.is_active ? 1 : 0.62,
            }}>
              {/* member */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                {u.picture ? (
                  <img src={u.picture} alt="" style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0 }} />
                ) : (
                  <div className="avatar-circle" style={{ width: 36, height: 36, flexShrink: 0, background: "var(--brand-gradient)", fontSize: 13 }}>
                    {initialsOf(u.name, u.email)}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.name}{isSelf && <span className="text-dim" style={{ fontSize: 11.5 }}> (you)</span>}
                  </div>
                  <div className="text-dim" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.title ? `${u.title} · ` : ""}{u.email}
                  </div>
                </div>
              </div>
              <div><RoleBadge role={u.role} /></div>
              <div><StatusPill active={u.is_active} /></div>
              <div className="text-soft" style={{ fontSize: 12.5 }}>{timeAgo(u.last_active_at)}</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <MiniStat icon={MessageSquare} n={u.stats?.conversations ?? 0} title="Conversations" />
                <MiniStat icon={Calendar} n={u.stats?.meetings ?? 0} title="Meetings" />
                <MiniStat icon={Database} n={u.stats?.vault_docs ?? 0} title="Vault notes" />
                <MiniStat icon={FileText} n={u.stats?.documents ?? 0} title="Documents" />
              </div>
              <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <IconBtn icon={Pencil} title="Edit" onClick={() => setEditing(u)} testid={`admin-edit-${u.user_id}`} />
                {!isSelf && <IconBtn icon={Send} title="Resend invite email" onClick={() => resendInvite(u)} testid={`admin-resend-${u.user_id}`} />}
                {!isSelf && <IconBtn icon={KeyRound} title="Reset password" onClick={() => resetPw(u)} testid={`admin-reset-${u.user_id}`} />}
                <IconBtn icon={Power} title={u.is_active ? "Deactivate" : "Reactivate"} onClick={() => toggleActive(u)} testid={`admin-toggle-active-${u.user_id}`} danger={u.is_active} />
                <IconBtn icon={Trash2} title="Delete" onClick={() => setConfirmDel(u)} testid={`admin-delete-${u.user_id}`} danger />
              </div>
            </div>
          );
        })}
       </div>
      </div>

      {adding && (
        <Modal eyebrow="New member" testid="admin-add-modal" onClose={() => setAdding(false)}>
          <AddUserForm onCreated={(created) => { setAdding(false); onShowTemp(created); reload(); }} />
        </Modal>
      )}
      {editing && (
        <Modal eyebrow="Edit member" testid="admin-edit-modal" onClose={() => setEditing(null)}>
          <EditUserForm user={editing} isSelf={editing.user_id === selfId} onSaved={() => { setEditing(null); reload(); }} />
        </Modal>
      )}
      {confirmDel && (
        <Modal eyebrow="Delete account" testid="admin-delete-modal" onClose={() => setConfirmDel(null)} maxWidth={420}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <h3 style={{ marginBottom: 0 }}>Delete {confirmDel.name}?</h3>
            <div className="text-soft" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
              This permanently removes <strong style={{ color: "var(--ink)" }}>{confirmDel.email}</strong> and all of their
              data — vault, meetings, conversations and documents. This can&apos;t be undone.
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDel(null)} style={{ flex: 1, justifyContent: "center" }}>Cancel</button>
              <button className="btn" onClick={() => doDelete(confirmDel)} data-testid="admin-delete-confirm"
                style={{ flex: 1, justifyContent: "center", background: "#E5564E", color: "#fff", border: "none" }}>
                <Trash2 size={15} /> Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- Insights tab ---------------- */
function InsightsTab({ insights }) {
  if (!insights) return <div className="text-dim" style={{ padding: 24 }}>Loading insights…</div>;
  const t = insights.totals;
  return (
    <div data-testid="admin-insights-tab" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <StatCard label="Team members" value={t.total_users} icon={Users} accent="var(--ph-brand-violet)" testid="admin-stat-total-users" />
        <StatCard label="Active accounts" value={t.active_users} icon={ShieldCheck} testid="admin-stat-active-users" />
        <StatCard label="Active this week" value={t.active_this_week} icon={Activity} accent="var(--gold)" testid="admin-stat-active-week" />
        <StatCard label="Super admins" value={t.super_admins} icon={Crown} accent="var(--ph-brand-violet)" testid="admin-stat-super-admins" />
        <StatCard label="Meetings logged" value={t.total_meetings} icon={Calendar} testid="admin-stat-meetings" />
        <StatCard label="Conversations" value={t.total_conversations} icon={MessageSquare} testid="admin-stat-conversations" />
        <StatCard label="Vault notes" value={t.total_vault_docs} icon={Database} testid="admin-stat-vault" />
        <StatCard label="Documents" value={t.total_documents} icon={FileText} testid="admin-stat-documents" />
      </div>

      {/* role + status overview */}
      <div className="panel" style={{ padding: 20 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-dim)", marginBottom: 14 }}>
          Permissions overview
        </div>
        <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
          <div><div className="text-dim" style={{ fontSize: 12 }}>Super Admins</div><div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)" }}>{t.super_admins}</div></div>
          <div><div className="text-dim" style={{ fontSize: 12 }}>Members</div><div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)" }}>{t.members}</div></div>
          <div><div className="text-dim" style={{ fontSize: 12 }}>Active</div><div style={{ fontSize: 22, fontWeight: 700, color: "var(--ph-product-teal)" }}>{t.active_users}</div></div>
          <div><div className="text-dim" style={{ fontSize: 12 }}>Deactivated</div><div style={{ fontSize: 22, fontWeight: 700, color: "#E5564E" }}>{t.deactivated_users}</div></div>
        </div>
      </div>

      {/* per-user activity */}
      <div className="panel" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 12px", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-dim)" }}>
          Activity by member
        </div>
       <div className="table-scroll-x">
        <div className="admin-row-insights" style={{
          display: "grid", gridTemplateColumns: "minmax(200px,2fr) 120px repeat(4, 90px)", gap: 10, padding: "8px 20px",
          borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)", fontSize: 10.5,
          letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-dim)",
        }}>
          <div>Member</div><div>Last active</div><div>Chats</div><div>Meetings</div><div>Notes</div><div>Docs</div>
        </div>
        {insights.per_user.map((u) => (
          <div key={u.user_id} className="admin-row-insights" data-testid={`admin-insights-row-${u.user_id}`} style={{
            display: "grid", gridTemplateColumns: "minmax(200px,2fr) 120px repeat(4, 90px)", gap: 10, alignItems: "center",
            padding: "12px 20px", borderBottom: "1px solid var(--hairline-soft)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <div className="avatar-circle" style={{ width: 30, height: 30, flexShrink: 0, background: "var(--brand-gradient)", fontSize: 11 }}>
                {initialsOf(u.name, u.email)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
                <div className="text-dim" style={{ fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
              </div>
            </div>
            <div className="text-soft" style={{ fontSize: 12.5 }}>{timeAgo(u.last_active_at)}</div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{u.stats?.conversations ?? 0}</div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{u.stats?.meetings ?? 0}</div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{u.stats?.vault_docs ?? 0}</div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>{u.stats?.documents ?? 0}</div>
          </div>
        ))}
       </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("team");
  const [users, setUsers] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tempData, setTempData] = useState(null);

  const reload = async () => {
    try {
      const [u, i] = await Promise.all([api.get("/admin/users"), api.get("/admin/insights")]);
      setUsers(u.data.users || []);
      setInsights(i.data);
    } catch {
      toast.error("Couldn't load the admin console.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [u, i] = await Promise.all([api.get("/admin/users"), api.get("/admin/insights")]);
        setUsers(u.data.users || []);
        setInsights(i.data);
      } catch {
        toast.error("Couldn't load the admin console.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (user && user.role !== "super_admin") return <Navigate to="/chat" replace />;

  return (
    <div data-testid="admin-page" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="surface-header">
        <div className="surface-title">Super Admin</div>
        <span className="status-pill"><span className="dot" />{users.length} member{users.length === 1 ? "" : "s"}</span>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "24px clamp(14px, 4vw, 32px) 60px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          {/* tabs */}
          <div className="seg" style={{ marginBottom: 24, maxWidth: 320 }} data-testid="admin-tabs">
            <button className={tab === "team" ? "active" : ""} onClick={() => setTab("team")} data-testid="admin-tab-team">
              <Users size={14} style={{ marginRight: 6 }} /> Team
            </button>
            <button className={tab === "insights" ? "active" : ""} onClick={() => setTab("insights")} data-testid="admin-tab-insights">
              <BarChart3 size={14} style={{ marginRight: 6 }} /> Insights
            </button>
          </div>

          {loading ? (
            <div className="text-dim" style={{ padding: 40, textAlign: "center" }}>
              <Loader2 size={18} className="spin" style={{ marginBottom: 8 }} /><div>Loading team…</div>
            </div>
          ) : tab === "team" ? (
            <TeamTab users={users} reload={reload} selfId={user?.user_id} onShowTemp={setTempData} />
          ) : (
            <InsightsTab insights={insights} />
          )}
        </div>
      </div>

      {tempData && (
        <Modal eyebrow="Account ready" testid="admin-temp-modal" onClose={() => setTempData(null)} maxWidth={460}>
          <TempPasswordView data={tempData} onClose={() => setTempData(null)} />
        </Modal>
      )}
    </div>
  );
}
